interface HrRecurrence {
  quantity: number;
  unit: string;
}

const statusLabels: Record<string, string> = {
  active: 'Zaradený',
  inactive: 'Vyradený',
  archived: 'Vyradený',
  completed: 'Dokončený',
  validated: 'Overený',
  committed: 'Importovaný',
  failed: 'Zlyhaný',
  cancelled: 'Zrušený',
  expired: 'Expirovaný',
  pending: 'Čakajúci',
};

const auditActionLabels: Record<string, string> = {
  'person-created': 'Osoba vytvorená',
  'person-updated': 'Osoba upravená',
  'person-deactivated': 'Osoba vyradená',
  'person-reactivated': 'Osoba zaradená',
  'evidence-values-replaced': 'Evidenčné hodnoty zmenené',
  'file-added': 'Súbor pridaný',
  'file-renamed': 'Súbor premenovaný',
  'file-reclassified': 'Kategória súboru zmenená',
  'file-deleted': 'Súbor vymazaný',
  'file-archived': 'Súbor vyradený',
  'file-restored': 'Súbor zaradený',
  'term-type-created': 'Typ termínu vytvorený',
  'term-type-updated': 'Typ termínu upravený',
  'term-type-archived': 'Typ termínu vyradený',
  'term-type-reactivated': 'Typ termínu zaradený',
  'file-category-created': 'Kategória súboru vytvorená',
  'file-category-updated': 'Kategória súboru upravená',
  'file-category-archived': 'Kategória súboru vyradená',
  'file-category-reactivated': 'Kategória súboru zaradená',
  'term-created': 'Termín vytvorený',
  'term-updated': 'Termín upravený',
  'term-executed': 'Termín vykonaný',
  'term-completed': 'Termín dokončený',
  'term-archived': 'Termín vyradený',
  'term-reactivated': 'Termín zaradený',
  'certificate-paired': 'Osvedčenie spárované',
  'certificate-unpaired': 'Osvedčenie odpárované',
  'certificate-requalified': 'Osvedčenie obnovené',
  execution: 'Vykonanie termínu',
};

/** Returns a Slovak label while keeping transport lifecycle slugs out of the UI. */
export function formatHrStatus(status?: string) {
  return status ? statusLabels[status] ?? 'Neznámy stav' : '';
}

/** Returns the user-facing HR term-kind label. */
export function formatHrTermKind(kind?: string) {
  if (kind === 'ordinary') return 'Bežný';
  if (kind === 'technician-certificate') return 'Osvedčenie technika';
  return kind ? 'Neznámy druh' : '';
}

/** Resolves the derived deadline state with the terminal lifecycle as its authoritative fallback. */
export function resolveHrDeadlineState(state?: string | null, status?: string | null) {
  return state ?? (status === 'completed' ? 'completed' : undefined);
}

/** Returns the user-facing deadline state. */
export function formatHrDeadlineState(state?: string | null, status?: string | null) {
  const resolvedState = resolveHrDeadlineState(state, status);
  if (resolvedState === 'upcoming') return 'Nadchádzajúci';
  if (resolvedState === 'overdue') return 'Po termíne';
  if (resolvedState === 'completed') return 'Dokončený';
  return resolvedState ? 'Neznáma lehota' : '';
}

/** Formats recurrence with Slovak singular and plural units. */
export function formatHrRecurrence(recurrence?: HrRecurrence | null) {
  if (!recurrence) return 'Jednorazový';
  const forms: Record<string, [string, string, string]> = {
    days: ['deň', 'dni', 'dní'],
    weeks: ['týždeň', 'týždne', 'týždňov'],
    months: ['mesiac', 'mesiace', 'mesiacov'],
    years: ['rok', 'roky', 'rokov'],
  };
  const unitForms = forms[recurrence.unit];
  if (!unitForms) return 'Neznáme opakovanie';
  const unit =
    recurrence.quantity === 1
      ? unitForms[0]
      : recurrence.quantity >= 2 && recurrence.quantity <= 4
      ? unitForms[1]
      : unitForms[2];
  return `${recurrence.quantity} ${unit}`;
}

/** Formats a recurrence unit for its dedicated export column. */
export function formatHrRecurrenceUnit(unit?: string) {
  return (
    {
      days: 'Dni',
      weeks: 'Týždne',
      months: 'Mesiace',
      years: 'Roky',
    }[unit ?? ''] ?? ''
  );
}

/** Returns a localized immutable activity title. */
export function formatHrAuditAction(action?: string) {
  return action ? auditActionLabels[action] ?? 'HR udalosť' : 'HR udalosť';
}
